import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  Divider, 
  IconButton, 
  Grid, 
  DialogContentText,
} from '@mui/material';
import { DateTime } from 'luxon';
import CloseIcon from '@mui/icons-material/Close';
import { PropTypes } from 'prop-types';
import { useEffect, useState, useRef, useContext } from 'react';
import { generateCurrentHourIntervals } from '../utils/RideTimeIntervals';
import { useBebackApi } from '../hooks/useBebackApi';
import LyftRideForm from './LyftRideForm';
import LyftRidesList from './LyftRidesList';
import FeedbackContext from '../context/FeedbackContext';
import FeedbackPopUp from './FeedbackPopUp';
import { LYFT_COUNTDOWN_TOKEN_TO_PROGRESS, LYFT_RIDE_TOKEN_SECONDS } from '../utils/constants';
import StoreContext from '../context/StoreContext';
function LyftDialog({open, onClose, firstName, lastName, phoneNumber, WO, pickupAddress, dropOffAddress, storeNo, onOpenLyftStatusDialog}) {
  const { searchRide, requestRide, updatePhone } = useBebackApi();
  const {time_zone, address} = useContext(StoreContext)
  const RIDE_TIME_INTERVALS = generateCurrentHourIntervals(time_zone);
  const EXCLUDED_FIELDS = ['internalNote', 'isRoundTrip'];
  const { setIsLoading } = useContext(FeedbackContext);

  const initialRideData = {
    firstName: firstName,
    lastName: lastName,
    phoneNumber: phoneNumber,
    pickupAddress: { 
      label: pickupAddress.address,
      lng: pickupAddress.lng,
      lat: pickupAddress.lat
    },
    pickupTime: RIDE_TIME_INTERVALS[0],
    isRoundTrip: false,
    dropOffAddress: {
      label: dropOffAddress,
      lng: null,
      lat: null
    },
    internalNote: '',
    WO: WO,
  }

  const [rideData, setRideData] = useState(initialRideData)
  const [rideSelectedIndex, setRideSelectedIndex] = useState(null);
  const [ridesList, setRidesList] = useState(null);
  const [requestRideButton, setRequestRideButton] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState('');
  const [popUpTitle, setPopUpTitle] = useState('');
  const [popUpType, setPopUpType] = useState('');
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null); // Ref to store the interval ID
  const progressRef = useRef(null);
  const { validateAddress } = useBebackApi();

  useEffect(() => {
    const fetchDropOffAddress = async () => {
      setIsLoading(true)
      if (!dropOffAddress) {
        setRideData((prev) => ({ ...prev, dropOffAddress: '' }));
        setIsLoading(false)
        return;
      }

      try {
        const suggestions = await validateAddress({
          addressQuery: dropOffAddress,
          lng: address.lng,
          lat: address.lat
        })

        if (suggestions.length === 0) {
          setRideData((prev) => ({ ...prev, dropOffAddress: '' }));
          setIsLoading(false)
          return;
        }

        const place = suggestions[0]
        
        const addressData = {
          label: place.label,
          lng: place.coordinates[0],
          lat: place.coordinates[1]
        }

        setRideData((prev) => ({ ...prev, dropOffAddress: addressData }));
      } catch (error) {
        console.error('Error fetching drop-off address:', error);
        setRideData((prev) => ({ ...prev, dropOffAddress: '' }));
      } finally {
        setIsLoading(false)
      }
    };

    fetchDropOffAddress();
  }, [])

  const handleListItemClick = (event, index) => {
    setRequestRideButton(true)
    setRideSelectedIndex(index);
  };

  const handleInputChange = (setter) => (value, field) => {
    const isAddressField = field === 'pickupAddress' || field === 'dropOffAddress';
    
    const fieldValue = isAddressField && value !== '' 
      ? {
          label: value.label,
          lng: value.coordinates[0],
          lat: value.coordinates[1]
        }
      : value;

    setter((prev) => ({ ...prev, [field]: fieldValue }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    stopFetching();
  };

  const onClosePopup = () => {
    setShowPopup(false)
  }

  const isValidForm = () => {
    const newErrors = Object.keys(rideData).reduce((acc, key) => {
      if (!rideData[key] && !EXCLUDED_FIELDS.includes(key)) {
        acc[key] = `${key.charAt(0).toUpperCase() + key.slice(1)} is required`;
      }
      if(key === 'phoneNumber' && rideData[key].length !== 10) {
        acc[key] = `${key.charAt(0).toUpperCase() + key.slice(1)} is invalid`;
      }
      return acc;
    }, {});

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;

  }

  const handleSearchRide = async () => {
    try {
      const timeToISO = rideData.pickupTime !== 'NOW' ? DateTime.fromFormat(rideData.pickupTime, 'h:mm').toISO() : rideData.pickupTime;
      const reqBody = {
        pickup_address: rideData.pickupAddress,
        dropoff_address: rideData.dropOffAddress,
        pickup_time: timeToISO,
        store_no: storeNo
      }
      setIsLoading(true)
      const searchRideRes = await searchRide(reqBody)
      setIsLoading(false)
      if(searchRideRes?.frontend_error_message){ 
        throw new Error(searchRideRes.frontend_error_message);
      }
      setProgress(100);
      setRidesList(searchRideRes);
    } catch (e) {
      console.log(e)
      stopFetching();
      setPopUpTitle('Something went wrong!')
      setPopUpMessage(e.message)
      setPopUpType('error')
      setShowPopup(true)
    }
  }

  const handleSendRideRequest = async () => {
    try {
      setIsLoading(true)
      const rideSelected = ridesList.cost_estimates[rideSelectedIndex];
      const reqBody = {
        wo: WO,
        store_no: storeNo,
        pickup_address: rideData.pickupAddress,
        dropoff_address: rideData.dropOffAddress,
        customer_first_name: rideData.firstName,
        customer_last_name: rideData.lastName,
        customer_phone_number: rideData.phoneNumber,
        cost_estimate: rideSelected,
        ...(ridesList.pick_time_ms && { pickup_time_ms: ridesList.pick_time_ms }),
        is_roundtrip: rideData.isRoundTrip
      }
      // Phone update
      if(phoneNumber !== rideData.phoneNumber) {
        const phoneReqBody = { wo:WO, newPhone: rideData.phoneNumber, siteNo: storeNo };
        const phoneUpdateRes = await updatePhone(phoneReqBody); 
        if(phoneUpdateRes === undefined){ 
          throw new Error('Error updating phone number. Please try again later.');
        }
      }
      // Send the ride request
      const requestRideRes = await requestRide(reqBody)
      if(requestRideRes?.frontend_error_message){ 
        const errorMsg = typeof requestRideRes.frontend_error_message === 'string' 
          ? requestRideRes.frontend_error_message 
          : requestRideRes.frontend_error_message.frontend_error_message || 'Unknown error';
        throw new Error(errorMsg);
      }
      onOpenLyftStatusDialog();
      onClose();
    } catch (e) {
      console.log(e.message)
      setPopUpTitle('Something went wrong!')
      setPopUpMessage(e.message)
      setPopUpType('error')
      setShowPopup(true)
    } finally {
      stopFetching();
      setIsLoading(false)
      setRequestRideButton(false);
      setRidesList(null)
      setRideSelectedIndex(null)
    }
  }

  const searchRideByInterval = () => {
    // Stop the previous interval
    stopFetching();
    // Validate and call the function immediately when the button is clicked
    if(!isValidForm()) {
      return;
    }
    handleSearchRide();
    
    // Set up the interval to call the function every 50 seconds
    if (!intervalRef.current) {
      intervalRef.current = setInterval(async () => {
        setRideSelectedIndex(null)
        await handleSearchRide();
      }, LYFT_RIDE_TOKEN_SECONDS*1000); // 60000ms = 1 minute
    }

    if (!progressRef.current) {
      progressRef.current = setInterval(() => {
        setProgress((prev) => (prev > 0 ? prev - (1*LYFT_COUNTDOWN_TOKEN_TO_PROGRESS) : (LYFT_RIDE_TOKEN_SECONDS*LYFT_COUNTDOWN_TOKEN_TO_PROGRESS)));
      }, 1000); // Every second
    }

  };

  // Stop the interval
  const stopFetching = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      clearInterval(progressRef.current);
      intervalRef.current = null
      progressRef.current = null;
      console.log('Interval stopped due to form change or by click the search ride button again.');
    }
  };

  useEffect(() => {
    if(requestRideButton || ridesList) {
      setRequestRideButton(false);
      setRidesList(null)
      setRideSelectedIndex(null)
    }
  }, [rideData])

  useEffect(() => {
    // Cleanup interval on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        clearInterval(progressRef.current)
      }
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="lyftDialog-title"
      aria-describedby="lyftDialog-description"
      scroll='paper'
      maxWidth='xl'
      fullWidth={true}
    >

      <Divider className='border-yellow border-4'/>

      <DialogTitle id="lyftDialog-title" className='flex justify-between items-center text-base uppercase font-extrabold'>
        Start ride request
        <IconButton 
          aria-label='close modal'
          onClick={onClose}
        >
          <CloseIcon className='p-0'/>
        </IconButton>
      </DialogTitle>

      <Divider className='mx-5'/>

      <DialogContent className='w-100 justify-around mb-5'>
        <DialogContentText id="lyftDialog-description" className='break-words mb-2 font-bold'>
          Enter ride details
        </DialogContentText>
        <Grid container spacing={2}>
          <LyftRideForm
            rideData={rideData}
            handleInputChange={handleInputChange}
            setRideData={setRideData}
            rideTimeIntervals={RIDE_TIME_INTERVALS}
            errors={errors}
          />
          <LyftRidesList
            ridesList={ridesList}
            handleListItemClick={handleListItemClick}
            requestRideButton={requestRideButton}
            rideSelectedIndex={rideSelectedIndex}
            handleSendRideRequest={handleSendRideRequest}
            searchRideByInterval={searchRideByInterval}
            progress={progress}
          />
        </Grid>
      </DialogContent>

      {
        showPopup && 
        <FeedbackPopUp
          open={showPopup}
          onClose={onClosePopup}
          title={popUpTitle}
          message={popUpMessage}
          type={popUpType}
        />
      }

    </Dialog>
  )
}

LyftDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  firstName: PropTypes.string, 
  lastName: PropTypes.string, 
  phoneNumber: PropTypes.string, 
  WO: PropTypes.string,
  pickupAddress: PropTypes.shape({
    address: PropTypes.string,
    lat: PropTypes.string,
    lng: PropTypes.string
  }),
  dropOffAddress: PropTypes.string,
  storeNo: PropTypes.string
};

LyftDialog.defaultProps = {
  open: false,
  onClose: () => {},
  firstName: '', 
  lastName: '', 
  phoneNumber: '', 
  WO: '',
  pickupAddress: {},
  dropOffAddress: '',
  storeNo: ''
};

export default LyftDialog;